<template>
  <div
    class="w-full min-w-rs-header h-[40px] bg-rs-content flex text-[13px] shadow-rs-nav z-50"
  >
    <div class="min-w-rs-content mx-auto flex">
      <nav class="nav-menu grow">
        <ul class="h-full">
          <li
            class="px-rs-padding-main h-full text-rs-nav float-left flex cursor-default"
            v-for="menuItem in menuItems"
          >
            <span class="flex items-center whitespace-nowrap">
              {{ t(menuItem.title) }}
            </span>
            <ul
              class="nav-submenu absolute mt-[40px] -ml-rs-padding-main flex flex-col max-w-[300px] shadow-rs-nav"
            >
              <li v-for="subItem in menuItem.subItems">
                <component
                  v-if="!subItem.hide"
                  :is="subItem.href ? 'a' : subItem.to ? 'router-link' : 'span'"
                  v-bind:[getHref(subItem.href)]="subItem.href"
                  :to="subItem.to"
                  @click="subItem.click ? subItem.click() : null"
                  class="px-5 py-2 block cursor-pointer"
                >
                  {{ t(subItem.title) }}
                  <div v-if="subItem.description" class="mt-2 ml-4 text-xs">
                    {{ t(subItem.description) }}
                  </div>
                </component>
              </li>
            </ul>
          </li>
        </ul>
      </nav>

      <div
        class="language_selection pr-rs-padding-main flex select-none text-rs-nav whitespace-nowrap"
      >
        <div>
          <button
            class="h-full"
            :class="{ 'font-bold': locale === 'de' }"
            :disabled="locale === 'de'"
            @click="setLocale('de')"
          >
            de
          </button>
          |
          <button
            class="h-full"
            :class="{ 'font-bold': locale === 'en' }"
            :disabled="locale === 'en'"
            @click="setLocale('en')"
          >
            en
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";

import useConfig from "@/composables/config";
import useUser from "@/composables/user";
import useLocale from "@/composables/locale";

const { config } = useConfig();
const { user, logout } = useUser();
const { t, locale } = useI18n({ useScope: "global" });
const { setSessionLocale } = useLocale();

function setLocale(newLocale: string) {
  locale.value = newLocale;
  setSessionLocale(newLocale);
}

function getHref(href: string | undefined) {
  return href ? "href" : "data-href";
}

type SubMenuItem = {
  title: string;
  description?: string;
  hide?: boolean;
  href?: string;
  to?: { name: string };
  click?: CallableFunction;
};

type MenuItem = {
  title: string;
  subItems: SubMenuItem[];
};

const menuItems: MenuItem[] = [
  {
    title: "top_nav_search",
    subItems: [
      {
        to: { name: "SearchPublications" },
        title: "top_nav_search_freidok",
      },
      {
        to: { name: "SearchProjects" },
        title: "top_nav_search_project",
      },
      {
        to: { name: "SearchPersons" },
        title: "top_nav_search_person",
      },
      {
        to: { name: "SearchInstitutions" },
        title: "top_nav_search_institution",
      },
    ],
  },
  {
    title: "top_nav_publish",
    subItems: [
      {
        href: config.freidokBaseUrl + "/submission?mode=metadata",
        title: "top_nav_publish_metadata_submission",
        description: "top_nav_publish_metadata_submission_desc",
      },
      {
        href: config.freidokBaseUrl + "/site/submissionLogin?mode=pub",
        title: "top_nav_publish_submission",
        description: "top_nav_publish_submission_desc",
      },
      {
        href: config.freidokBaseUrl + "/submission/project",
        title: "top_nav_publish_project_submission",
        description: "top_nav_publish_project_submission_desc",
      },
      {
        href: config.freidokBaseUrl + "/submission/activity",
        title: "top_nav_publish_bwa_submission",
        description: "top_nav_publish_bwa_submission_desc",
      },
    ],
  },
  {
    title: "top_nav_myfreidok_reports",
    subItems: [
      {
        href: config.reportToolUrl,
        // to: { name: "ReportInstitution" },
        title: "top_nav_research_reports_institutional",
        description: "top_nav_research_reports_institutional_desc",
      },
      {
        href: config.freidokBaseUrl + "/myFreidok/viewPersonalReport",
        // to: { name: "ReportPerson" },
        title: "top_nav_research_reports_personal",
        description: "top_nav_research_reports_personal_desc",
      },
    ],
  },
  {
    title: "top_nav_about_fis",
    subItems: [
      {
        to: { name: "Site", params: { type: "about" } },
        title: "fdt:ui:about",
      },
      {
        href: config.freidokBaseUrl + "/site/interfaces",
        title: "top_nav_about_interfaces",
      },
      {
        to: { name: "Help" },
        title: "fdt:ui:help",
      },
      {
        to: { name: "Site", params: { type: "contact" } },
        title: "fdt:ui:contact",
      },
    ],
  },
  {
    title: "top_nav_myfreidok",
    subItems: [
      {
        hide: !(user.isLoggedIn && user.isAdmin),
        to: { name: "AdminSearchPublications" },
        title: "top_nav_admin",
      },
      {
        hide: !(
          user.isLoggedIn &&
          !user.isAdmin &&
          (user.isContentAdmin || user.isConfigAdmin)
        ),
        href: config.freidokBaseUrl + "/admintools",
        title: "top_nav_admin",
      },
      {
        hide: user.isLoggedIn,
        href: config.freidokBaseUrl + "/myFreidok",
        title: "top_nav_login",
        description: "top_nav_login_desc",
      },
      {
        href: config.freidokBaseUrl + "/myFreidok/viewProfile",
        title: "top_nav_myfreidok_profile",
        description: "top_nav_myfreidok_profile_desc",
      },
      {
        href: config.freidokBaseUrl + "/myFreidok/profile",
        //to: { name: "EditPerson", params: { id: user.current?.person_id } },
        title: "top_nav_myfreidok_account",
        description: "top_nav_myfreidok_account_desc",
      },
      {
        hide: !config.orcidEnabled,
        to: { name: "OrcidOverview" },
        title: "top_nav_myfreidok_orcid",
        description: "top_nav_myfreidok_orcid_desc",
      },
      {
        to: { name: "UserSearchPublications" },
        title: "top_nav_myfreidok_entries",
        description: "top_nav_myfreidok_entries_desc",
      },
      {
        href: config.freidokBaseUrl + "/myFreidok/tan",
        title: "top_nav_myfreidok_tan_gen",
        description: "top_nav_myfreidok_tan_gen_desc",
      },
      {
        hide: !user.isLoggedIn,
        click: logout,
        title: "top_nav_logout",
      },
    ],
  },
];
</script>

<style>
.nav-menu > ul > li:hover .nav-submenu {
  visibility: visible;
}

.nav-submenu {
  visibility: hidden;
}

.nav-submenu > li {
  color: hsl(var(--color-text-rs-sub-nav));
  background-color: hsl(var(--color-bg-rs-sub-nav));
}

.nav-submenu > li :hover {
  color: hsl(var(--color-text-rs-sub-nav-hover));
  background-color: hsl(var(--color-bg-rs-sub-nav-hover));
}
</style>
